import { useState } from "react";
import { useQuery } from "react-query";
import { authAxios as axios } from "api-generate-types/src/axios";
import cx from "classnames";
import { mapToAudioModel, mapToVideoModel } from "shared-services";
import { MediaObject, MediaType, TranscodeStatus } from "shared-types";
import { useTranslation } from "i18n";
import { PopUpMediaViewer, Tooltip } from "../../../../index";
import { config } from "../../../../config";
import ThumbnailViewer from "../ThumbnailVideoViewer";

const mediaDataPollingInterval = 2000;
const getMediaPreviewQueryKey = "get-media-preview";

interface Props {
  id?: string;
  isProcessingVisible?: boolean;
  processingTooltipText?: string;
  fileNameLength?: number;
  isTooltipVisible?: boolean;
}

const MediaFilePreview = ({
  id,
  isProcessingVisible,
  processingTooltipText,
  fileNameLength,
  isTooltipVisible = true,
}: Props) => {
  const { t } = useTranslation();
  const [mediaPlayerPopIsOpen, setMediaPlayerPopIsOpen] = useState(false);
  const { data, isError } = useQuery(
    [getMediaPreviewQueryKey, id],
    async () => {
      const { data } = await axios.get<{ value: MediaObject }>(
        `${config.titaniumApiUrl}/media/${id}`
      );

      return data.value;
    },
    {
      enabled: !!id,
      retry: false,
      refetchInterval: (data, query) => {
        if (query.state.status === "error") return false;

        if (
          data &&
          ![TranscodeStatus.Awaiting, TranscodeStatus.Processing].includes(
            data.transcodeStatus
          )
        ) {
          return false;
        }

        return mediaDataPollingInterval;
      },
    }
  );
  const mediaType = data?.mediaType;
  const isComplete = data?.transcodeStatus === TranscodeStatus.Complete;
  let media;

  if (isComplete && mediaType === MediaType.Video) {
    media = mapToVideoModel(
      { ...data, title: data?.originalFileName, selectedThumbnail: 0 },
      data
    );
  }

  if (isComplete && mediaType === MediaType.Audio) {
    media = mapToAudioModel({ ...data, title: data?.originalFileName }, data);
  }

  const preview = (
    <div
      className={cx("mr-2 max-h-[60px] w-[100px]", {
        "bg-grey-fog-1": !isComplete,
      })}
    >
      <ThumbnailViewer
        playback={() => setMediaPlayerPopIsOpen(true)}
        thumbnailUrl={data?.thumbnails?.[0]}
        mediaType={data?.mediaType}
        isLoaded={isComplete}
        isProcessingVisible={isProcessingVisible}
        isUnavailable={isError}
      />
      {media && mediaType && (
        <PopUpMediaViewer
          isOpen={mediaPlayerPopIsOpen}
          onClose={() => setMediaPlayerPopIsOpen(false)}
          mediaType={mediaType}
          mediaList={[media]}
          fileNameLength={fileNameLength}
        />
      )}
    </div>
  );

  if (!isTooltipVisible) return preview;

  return (
    <Tooltip
      html={processingTooltipText || t("common:videoProcessing.tooltip.body")}
      disabled={isComplete}
    >
      {preview}
    </Tooltip>
  );
};

export default MediaFilePreview;
